import {Arc, DefaultArcObject} from 'd3';

const d3SVGArcOffset = -Math.PI / 2;

function functor<T>(v: (() => T | null) | T | null): () => T | null {
  return typeof v === 'function'
    ? (v as () => T | null)
    : function () {
        return v;
      };
}

export interface LinearArcDatum {
  startAngle: number;
  endAngle: number;
  radius: number;
  reverse: boolean;
}

function d3SVGArcInnerRadius<This>(d: DefaultArcObject): number | null {
  return d.innerRadius;
}

function d3SVGArcOuterRadius<This>(d: DefaultArcObject): number | null {
  return d.outerRadius;
}

function d3SVGArcStartAngle<This>(d: DefaultArcObject): number | null {
  return d.startAngle;
}

function d3SVGArcEndAngle<This>(d: DefaultArcObject): number | null {
  return d.endAngle;
}

export interface BenchlingArc<This> extends Arc<This, LinearArcDatum> {
  radius: (v: (() => number | null) | number) => ((d: LinearArcDatum) => number) | BenchlingArc<This>;
  reverse: (v: (() => boolean | null) | boolean) => ((d: LinearArcDatum) => boolean) | BenchlingArc<This>;
}

/**
 * Draws an arced line given the following parameters:
 * d = {
 *   radius: radius in pixels at which the arc should be drawn
 *   startAngle: the start angle. Derived from the start residue index on the feature. Measured in radians.
 *   endAngle: the end angle. Derived from the end residue index on the feature. Measured in radians.
 *   reverse: whether to reverse the arc direction.
 * }
 * Returns the `d` attribute of an SVG path.
 *
 * Generally, we draw the arc in a 5' -> 3' direction, relative to the strand on which a feature is being
 * drawn, or relative to the forward strand for a feature that doesn't care about strand.
 */
function linearArc<This>(): BenchlingArc<This> {
  let radius = (d: LinearArcDatum): number | null => d.radius;
  let startAngle = (d: LinearArcDatum): number | null => d.startAngle;
  let endAngle = (d: LinearArcDatum): number | null => d.endAngle;
  let reverse = (d: LinearArcDatum): boolean | null => d.reverse;

  /**
   * Calculates the svg `d` value for the linear arc, based on values previously set on the linearArc object.
   * Assumes that radius, startAngle, and endAngle have all already been set.
   */
  function arc(this: This, ...args: Array<unknown>): string {
    let arcAngle;
    /** Radius in pixels at which the arc should be drawn */
    const r0: number = radius.apply(this, args);
    /** The start angle, in radians counter-clockwise from the positive x-axis */
    let a0: number = startAngle.apply(this, args) + d3SVGArcOffset;
    /** The end angle, in radians counter-clockwise from the positive x-axis */
    let a1: number = endAngle.apply(this, args) + d3SVGArcOffset;
    /** Whether to reverse the arc direction */
    const shouldReverse = reverse.apply(this, args);

    if (a1 < a0) {
      arcAngle = a0;
      a0 = a1;
      a1 = arcAngle;
    }
    arcAngle = a1 - a0;

    // largeArc is a flag to give the path if the arc angle is >180deg. arcAngle is in radians.
    const largeArc = arcAngle < Math.PI ? '0' : '1';
    let sweep = '1';

    if (shouldReverse) {
      sweep = '0';
      [a1, a0] = [a0, a1];
    }

    const c0 = Math.cos(a0);
    const s0 = Math.sin(a0);
    const c1 = Math.cos(a1);
    const s1 = Math.sin(a1);
    const startX = r0 * c0;
    const startY = r0 * s0;
    const endX = r0 * c1;
    const endY = r0 * s1;
    return `M${startX},${startY}A${r0},${r0} 0 ${largeArc},${sweep} ${endX},${endY}`;
  }

  arc.reverse = function (v: () => boolean | null) {
    if (!arguments.length) {
      return reverse;
    }
    reverse = functor(v);
    return arc;
  };

  arc.radius = function (v: (() => number | null) | number) {
    if (!arguments.length) {
      return radius;
    }
    radius = functor(v);
    return arc;
  };

  arc.startAngle = function (v: () => number | null) {
    if (!arguments.length) {
      return startAngle;
    }
    startAngle = functor(v);
    return arc;
  };

  arc.endAngle = function (v: () => number | null) {
    if (!arguments.length) {
      return endAngle;
    }
    endAngle = functor(v);
    return arc;
  };

  return arc as unknown as BenchlingArc<This>;
}

/**
 * Creates constructor functions for Arc objects to draw common curved, approximately rectangular features,
 *   such as annotations and primers.
 * @param dir What direction the feature is pointing. 1 for a feature on the forward strand or a feature that
 *   doesn't care about strand, -1 for a feature on the reverse strand.
 * @param arrow What kind of arrow, if any, to put on the 3' end of the feature. 'arrow' is a flanged
 *   arrowhead, used for primers. 'triangle' is a pointed arrowhead, used for annotations. 'none' is a blunt
 *   end.
 */
function arcMaker<This>(
  dir: 1 | -1,
  arrow: 'arrow' | 'triangle' | 'none'
): () => Arc<This, DefaultArcObject> {
  return function (): Arc<This, DefaultArcObject> {
    let innerRadius = d3SVGArcInnerRadius;
    let outerRadius = d3SVGArcOuterRadius;
    let startAngle = d3SVGArcStartAngle;
    let endAngle = d3SVGArcEndAngle;

    /**
     * Calculates the svg `d` value for the outline of the feature, based on values previously set on the
     * object. Assumes that outerRadius, startAngle, and endAngle have already been set. If innerRadius is
     * unset, this will return a `d` value for an arced line.
     *
     * We always draw the path clockwise from a starting point near the 5' end of the forward strand of the
     * sequence. For features on the forward strand, this means the outer arc of the feature is drawn first;
     * for features on the reverse strand, this means the arrow is drawn first.
     */
    function arc(this: This, ...args: Array<unknown>): string {
      /** First angle of the full arc region, in radians */
      let fullAngle0: number;
      /** Second angle of the full arc region, in radians */
      let fullAngle1: number;

      let rInnerArc = innerRadius.apply(this, args);
      const rOuterArc = outerRadius.apply(this, args);
      const adjustedStartAngle = startAngle.apply(this, args) + d3SVGArcOffset;
      const adjustedEndAngle = endAngle.apply(this, args) + d3SVGArcOffset;

      if (adjustedEndAngle < adjustedStartAngle) {
        fullAngle0 = adjustedEndAngle;
        fullAngle1 = adjustedStartAngle;
      } else {
        fullAngle0 = adjustedStartAngle;
        fullAngle1 = adjustedEndAngle;
      }
      // Angle to stop drawing the arc and start drawing the arrow.
      const targetArcAngle = Math.min(Math.PI / 10, 10 / rInnerArc);

      /** First angle of only the arced portion without arrow, in radians */
      let arcAngle0: number;
      /** Second angle of only the arced portion without arrow, in radians */
      let arcAngle1: number;
      /** Angle size of the arced portion without arrow, in radians */
      let dArcAngle: number;
      /** cosine of fullAngle0, in a forward arc, or arcAngle0, in a reverse arc */
      let c0: number;
      /** cosine of arcAngle0, in a forward arc, or fullAngle0, in a reverse arc */
      let c1: number;
      /** sine of fullAngle0, in a forward arc, or arcAngle0, in a reverse arc */
      let s0: number;
      /** sine of arcAngle0, in a forward arc, or fullAngle0, in a reverse arc */
      let s1: number;
      if (dir === 1) {
        arcAngle0 = Math.max(fullAngle0, fullAngle1 - targetArcAngle);
        dArcAngle = arcAngle0 - fullAngle0;
        arcAngle1 = fullAngle1;

        c0 = Math.cos(fullAngle0);
        s0 = Math.sin(fullAngle0);
        c1 = Math.cos(arcAngle0);
        s1 = Math.sin(arcAngle0);
      } else {
        arcAngle0 = Math.min(fullAngle1, fullAngle0 + targetArcAngle);
        dArcAngle = fullAngle1 - arcAngle0;
        arcAngle1 = fullAngle0;

        c0 = Math.cos(arcAngle0);
        s0 = Math.sin(arcAngle0);
        c1 = Math.cos(fullAngle1);
        s1 = Math.sin(fullAngle1);
      }
      const cy = Math.cos(arcAngle1);
      const sy = Math.sin(arcAngle1);
      // largeArc is a flag to give the path if the arc angle is >180deg. dArcAngle is in radians.
      const largeArc = dArcAngle < Math.PI ? '0' : '1';

      /** The radius in pixels of the inner side of the arrow or triangle at the end of the arc */
      let rInnerArrow: number;
      /** The radius in pixels of the point of the arrow or triangle at the end of the arc */
      let rArrowPoint: number;
      switch (arrow) {
        case 'arrow':
          rInnerArrow = rInnerArc;
          rArrowPoint = rOuterArc;
          rInnerArc += (rOuterArc - rInnerArc) / 3;
          break;
        case 'triangle':
          rInnerArrow = rInnerArc;
          rArrowPoint = (rInnerArc + rOuterArc) / 2;
          break;
        case 'none':
        default:
          rInnerArrow = rInnerArc;
          rArrowPoint = rOuterArc;
          break;
      }

      if (rInnerArc) {
        // We always draw the feature clockwise, but start in different places depending on whether the
        // feature is on the forward or reverse strand.
        const innerArrowStartX = rInnerArrow * c0;
        const innerArrowStartY = rInnerArrow * s0;
        const innerArrowEndX = rInnerArrow * c1;
        const innerArrowEndY = rInnerArrow * s1;
        const arrowPointX = rArrowPoint * cy;
        const arrowPointY = rArrowPoint * sy;
        const outerArcStartX = rOuterArc * c0;
        const outerArcStartY = rOuterArc * s0;
        const outerArcEndX = rOuterArc * c1;
        const outerArcEndY = rOuterArc * s1;
        const innerArcStartX = rInnerArc * c1;
        const innerArcStartY = rInnerArc * s1;
        const innerArcEndX = rInnerArc * c0;
        const innerArcEndY = rInnerArc * s0;
        let startingPoint: string;
        let arrowPoint: string;
        const outerArc = `A${rOuterArc},${rOuterArc} 0,${largeArc},1 ${outerArcEndX},${outerArcEndY}`;
        const outerArcToInnerArc = `L${innerArcStartX},${innerArcStartY}`;
        const innerArc = `A${rInnerArc},${rInnerArc} 0,${largeArc},0 ${innerArcEndX},${innerArcEndY}`;
        if (dir === 1) {
          // Start at the point of the outer arc closest to the start of the feature, then draw the outer arc.
          // Next, draw the arrow tip. If we're using an 'arrow' arrow, we'll need to connect down to the
          // radius of the inner arc, but even in a 'triangle' or 'none' arrow, we add a no-op `L` for that.
          // Then draw the inner arc and close the path.
          startingPoint = `M${outerArcStartX},${outerArcStartY}`;
          arrowPoint = `L${arrowPointX},${arrowPointY} L${innerArrowEndX},${innerArrowEndY}`;
          return `${startingPoint} ${outerArc} ${arrowPoint} ${outerArcToInnerArc} ${innerArc} Z`;
        } else {
          // Start at the point of the inner arc closest to the end of the feature, then draw the arrow tip.
          // Draw the outer arc and connect back to the inner arc. Then draw the inner arc and close the path.
          startingPoint = `M${innerArrowStartX},${innerArrowStartY}`;
          arrowPoint = `L${arrowPointX},${arrowPointY} L${outerArcStartX},${outerArcStartY}`;
          return `${startingPoint} ${arrowPoint} ${outerArc} ${outerArcToInnerArc} ${innerArc} Z`;
        }
      } else {
        const startX = rOuterArc * c0;
        const startY = rOuterArc * s0;
        const endX = rOuterArc * c1;
        const endY = rOuterArc * s1;
        return `M${startX},${startY} A${rOuterArc},${rOuterArc} 0,${largeArc},1 ${endX},${endY} L0,0Z`;
      }
    }

    arc.innerRadius = function (v: () => number | null) {
      if (!arguments.length) {
        return innerRadius;
      }
      innerRadius = functor(v);
      return arc;
    };

    arc.outerRadius = function (v: () => number | null) {
      if (!arguments.length) {
        return outerRadius;
      }
      outerRadius = functor(v);
      return arc;
    };

    arc.startAngle = function (v: () => number | null) {
      if (!arguments.length) {
        return startAngle;
      }
      startAngle = functor(v);
      return arc;
    };

    arc.endAngle = function (v: () => number | null) {
      if (!arguments.length) {
        return endAngle;
      }
      endAngle = functor(v);
      return arc;
    };
    return arc as unknown as Arc<This, DefaultArcObject>;
  };
}

export default {
  forwardArc: arcMaker(1, 'triangle'),
  backwardArc: arcMaker(-1, 'triangle'),
  forwardArrowArc: arcMaker(1, 'arrow'),
  backwardArrowArc: arcMaker(-1, 'arrow'),
  linearArc,
};
